/**
 * Generic document fetch function
 * @param url - The endpoint URL
 * @param filterParams - Query parameters (optional)
 * @param init - Request options (optional)
 * @returns Promise<Response>
 */
export default function fetchDocument(url: string, filterParams?: URLSearchParams, init?: RequestInit): Promise<Response> {
    const fullUrl = filterParams ? `${url}?${filterParams.toString()}` : url;
    
    return fetch(fullUrl, {
        method: 'GET',
        credentials: 'same-origin',
        redirect: 'manual',
        ...init
    });
}

// Constants
export const downloadTimeout = 60000;

// Platform detection functions
const doesWindowHavePharmacyBridge = (): boolean => {
    return (typeof window === 'object')
        && (typeof window.pharmacyBridge === 'object');
}

const doesWindowHavePostMessage = (): boolean => {
    return (typeof window === 'object')
        && (typeof window.webkit === 'object')
        && (typeof window.webkit.messageHandlers === 'object')
        && (typeof window.webkit.messageHandlers.mshopwolfgang === 'object')
        && (typeof window.webkit.messageHandlers.mshopwolfgang.postMessage === 'function');
};

// File download functions
const promptDownload = (response: Blob, fileName: string) => {
    const fileUrl = window.URL.createObjectURL(response);
    const a = document.createElement('a');
    a.href = fileUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
}

const androidWriteFile = (data: any, fileName: string) => {
    window.pharmacyBridge.fileWriter(data, fileName);
}

const iosWriteFile = (action: string, data: any, fileName: string) => {
    window.webkit.messageHandlers.mshopwolfgang.postMessage({
        action: action,
        data: data,
        fileName: fileName
    });
}

// Error handling class
export class FetchReportError extends Error {
    filterOptionValue: string;

    constructor(name: string, filterOptionValue: string, message?: string) {
        super(message);
        this.name = name;
        this.filterOptionValue = filterOptionValue;
        Object.setPrototypeOf(this, FetchReportError.prototype);
    }
}

// Status callbacks interface for better type safety
export enum StatementStatus {
    Hidden,
    FilterOptions,
    Downloading,
    Done,
    GenerationError,
    NotFoundError,
}

// Generic document processing with error handling and mShop support
export const processDocumentResponse = async (
    responsePromise: Promise<Response>,
    options: {
        filterOptionValue: string;
        noStatementErrorMessage: string;
        fileName: string;
        mShop?: boolean;
        iosDevice?: boolean;
        androidDevice?: boolean;
        downloadComplete?: () => void;
    },
    abortDownload: { reason?: string }
): Promise<Blob> => {
    let rejected = false;
    
    return new Promise((resolve, reject) => {
        responsePromise
            .then(r => {
                if (r.status === 404 || r.status === 204) {
                    rejected = true;
                    reject(new FetchReportError("not-found", options.filterOptionValue, options.noStatementErrorMessage));
                } else if (r.type === "opaqueredirect") {
                    rejected = true;
                    window.location.reload();
                    reject(new FetchReportError("generation", options.filterOptionValue, undefined));
                } else if (r.status !== 200) {
                    rejected = true;
                    reject(new FetchReportError("generation", options.filterOptionValue, undefined));
                }
                return r.blob();
            })
            .then(blob => {
                if (options.mShop && !rejected) {
                    return new Promise((resolve, _) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result);
                        reader.readAsDataURL(blob);
                    });
                } else {
                    resolve(blob);
                }
            })
            .then(data => {
                if (!rejected && options.mShop && options.iosDevice && doesWindowHavePostMessage()) {
                    options.downloadComplete?.();
                    iosWriteFile('fileWriter', data, options.fileName);
                }
                else if (!rejected && options.mShop && options.androidDevice && doesWindowHavePharmacyBridge()) {
                    options.downloadComplete?.();
                    androidWriteFile(data, options.fileName);
                }
            })
            .catch(error => {
                if (error.name === "AbortError" && abortDownload.reason === "hide") {
                    reject(new FetchReportError("hide", options.filterOptionValue, undefined));
                } else {
                    reject(new FetchReportError("generation", options.filterOptionValue, undefined));
                }
            });
    });
}

export const handleDownloadClick = async (
    filterOptionResult:any,
    fetchPromise: (filterOptionResult:any) => Promise<Blob>,
    fileName: string,
    setStatus: (status: any) => void,
    setDownloadError: (error: FetchReportError) => void,
    iosDevice?: boolean,
    mShop?: boolean
) => {
    setStatus(StatementStatus.Downloading);
    
    try {
        const response = await fetchPromise(filterOptionResult);
        
        if (iosDevice) {
            await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        if (!mShop) {
            promptDownload(response, fileName);
            setStatus(StatementStatus.Done);
        }
        
    } catch (error) {
        if (error instanceof FetchReportError) {
            switch (error.name) {
                case "generation": {
                    setDownloadError(error);
                    setStatus(StatementStatus.GenerationError);
                    break;
                }
                case "not-found": {
                    setDownloadError(error);
                    setStatus(StatementStatus.NotFoundError);
                    break;
                }
                case "hide": {
                    break;
                }
                default: {
                    setDownloadError(error);
                    setStatus(StatementStatus.GenerationError);
                    break;
                }
            }
        } else {
            console.error("Unknown error type:", error);
        }
    }
}
